
<template>
  <div
    class="chart-wrapper"
    :style="{ height: computedHeight }"
    :aria-label="ariaLabel"
    role="img"
  >
    <!-- Optional toolbar (download) -->
    <div v-if="downloadable" class="chart-toolbar">
      <v-btn
        icon
        density="comfortable"
        variant="tonal"
        color="primary"
        class="chart-download-btn"
        :title="`Diagramm als PNG herunterladen`"
        @click="downloadImage"
      >
        <v-icon>mdi-download</v-icon>
      </v-btn>
    </div>

    <!--
      vue-chartjs v5.

      `v-if="bereit"` sorgt dafür, dass `.chart-wrapper` im Dokument steht,
      bevor der Canvas darin entsteht. Das gibt dem Diagramm von Anfang an
      seine wirkliche Größe — sonst misst Chart.js einen Container ohne
      Ausdehnung und skaliert beim ersten Resize sichtbar nach.

      Gegen den Absturz in `chartjs-plugin-datalabels` hilft das
      ausdrücklich **nicht** — eine frühere Notiz an dieser Stelle behauptete
      das und hatte die Richtung verdreht. Der Absturz braucht einen
      eingehängten Canvas, nicht einen losgelösten; die Begründung steht in
      `datalabelsGuard.js`, und von dort kommt auch die Absicherung.

      `:redraw="true"` stand hier bis jetzt — die Eigenschaft gibt es in
      vue-chartjs v5 nicht mehr (sie ist aus v4) und landete unbemerkt als
      Attribut am Canvas.
    -->
    <Line
      v-if="bereit"
      ref="chartRef"
      :data="chartData"
      :options="mergedOptions"
      :height="null"
      :width="null"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { useChartDownload } from '@/composables/useChartDownload'
import { normalizeChartOptions } from './chartOptions'
import { datalabelsGuard } from './datalabelsGuard'

ChartJS.register(
  Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale,
  ChartDataLabels, datalabelsGuard,
)

const props = defineProps({
  chartData: { type: Object, required: true },
  // Optional: die VALERI-Seiten reichen teils gar keine Optionen herein.
  options:   { type: Object, default: () => ({}) },

  height:       { type: [Number, String], default: 320 },
  ariaLabel:    { type: String, default: 'Liniendiagramm' },
  downloadable: { type: Boolean, default: false },

  projectName: { type: String, default: '' },
  methodName:  { type: String, default: '' },
  filename:    { type: String, default: '' },
})

const chartRef = ref(null)

/*
 * Erst rendern, wenn `.chart-wrapper` im Dokument steht — siehe die
 * Begründung am `v-if` im Template. Ein `nextTick` nach `onMounted` genügt:
 * danach ist der eigene Wurzelknoten eingehängt, und der Canvas entsteht
 * darin statt losgelöst.
 */
const bereit = ref(false)
onMounted(async () => {
  await nextTick()
  bereit.value = true
})

const computedHeight = computed(() => {
  return typeof props.height === 'number' ? `${props.height}px` : props.height
})

/*
 * Die VALERI-Seiten reichen Optionen im alten Chart.js-2-Format herein
 * (`legend`, `title`, `tooltips` auf oberster Ebene). normalizeChartOptions
 * hebt sie nach `plugins.*`, sonst fehlen Titel und Legende wortlos.
 */
const normalizedOptions = computed(() => normalizeChartOptions(props.options))

const mergedOptions = computed(() => {
  const base = {
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 200,
    animation: { duration: 300, easing: 'easeOutQuart' },
  }
  return {
    ...base,
    ...normalizedOptions.value,
    plugins: {
      ...(normalizedOptions.value?.plugins || {}),
    },
  }
})

// Der Chart selbst wird nach außen gereicht: der Excel-Export der
// VALERI-Seiten holt sich darüber das gerenderte Bild.
defineExpose({ get chart() { return chartRef.value?.chart } })

const { downloadImage } = useChartDownload(chartRef, () => ({
  titleText: normalizedOptions.value?.plugins?.title?.text,
  projectName: props.projectName,
  methodName: props.methodName,
  filename: props.filename,
}))
</script>

<style scoped>
.chart-wrapper {
  position: relative;

  /* Volle Breite des Containers — wie `.chart-wrapper` in BarChart.vue.
     Die Höhe kommt aus der :style-Bindung (computedHeight).

     Hier standen `width: 450px` und `min-height: 450px`. Die 450 sind die
     Breite des Vorschau-Layouts (`.preview-chart` in PreviewChart.vue) und
     waren in die geteilte Komponente gewandert. Für die Vorschau passte das,
     für jeden anderen Einbauort nicht: in den VALERI-Karten (gut 590 px breit)
     nagelte `width: 450px` das Diagramm fest, `max-width: 100%` verhindert nur
     das Überlaufen und lässt es nicht mitwachsen — es blieben rund 140 px
     toter Raum neben der Kurve. Genau das sah gequetscht aus, und weil
     `collectValeriChartsForDocx` den Canvas in seiner Seitengröße übernimmt,
     stand es so auch im Bericht.

     Das `min-height: 450px` kostete zusätzlich die Ausrichtung: die
     Nachbarkarte derselben Reihe trägt ein Balkendiagramm ohne eigenen
     Wrapper und folgt damit den 420 px der Seite. Die beiden Karten waren
     also 30 px verschieden hoch.

     Wer eine feste Breite oder Höhe braucht, setzt sie am Einbauort —
     PreviewAmortisation.vue tut das jetzt. */
  width: 100%;
  max-width: 100%;
}

.chart-toolbar {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
}

.chart-download-btn {
  backdrop-filter: saturate(140%) blur(2px);
  border-radius: 10px;
}

:deep(canvas) {
  border-radius: 8px;
}
</style>
